import * as THREE from 'three';
import { basic, cloth, emis, smoke, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { Fork, kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Heraldo de la tormenta (era IV, docs/CRIATURAS.md «Bastión de la Vigilia»): the weather of the titan's back
 * dreamt into a spirit. A mass of black storm cloud that hangs in the air, lit from inside by veins of
 * blue-white lightning that crawl and fork (they change with every frame); on top, a deep hood of rag with
 * nothing in it but two pale points and the hint of a jaw; two long arms of cloud ending in claws that crackle;
 * underneath, tendrils of cloud that hang and trail like rain falling from a squall.
 *
 * It flies: the hover is its rest pose (`HERALD_HOVER`; `die` drops it). Bones: body (the cloud), hood, armL/
 * foreL and armR/foreR, five tendrils in two pieces (tA1/tA2 … tE1/tE2), `charge` (a ball of lightning between
 * its hands, built tiny: the cast clip scales it by `1 / CHARGE_REST`) and `bolt` (a fork of lightning thrown
 * down and forward from it, built tiny the same way). The cloud's own crawl is in the frames (no lights).
 */

export const HERALD_HOVER = 1.45;
/** How small the ball of lightning and the bolt are built (the clips scale them by about 1 / CHARGE_REST). */
export const CHARGE_REST = 0.02;

export const TENDRILS = ['A', 'B', 'C', 'D', 'E'] as const;

export const stormHerald = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 977);
  const { g, rng } = c;
  const k4 = kit(rng);
  g.position.y = HERALD_HOVER;

  const cloud = std('#1b1d27', { roughness: 1, flatShading: true, bumpScale: 0.04 });
  const cloudL = std('#454d68', { roughness: 1, flatShading: true, bumpScale: 0.04 });
  const haze = smoke('#3a4157', 0.5, { flatShading: true });
  const rag = cloth('#121319', { side: THREE.DoubleSide });
  const voidM = std('#050507', { roughness: 1, bumpScale: 0 });
  const bolt = emis('#f4f8ff', '#9cc8ff', 3.2);
  const rainM = basic('#5d6d8c', 0.35);

  /* ---------- the cloud: a tumbled mass of black puffs, wider on top, a haze of lighter cloud round it ---------- */
  const bodyB = joint(g, V(0, 0.1, 0), 'body');
  const body = bodyB.abs;
  const puffs: [number, number, number, number][] = [
    [0, 0.12, 0, 0.42], [0.36, 0.26, -0.05, 0.32], [-0.37, 0.24, -0.02, 0.33], [0.05, 0.4, -0.18, 0.36],
    [0.16, -0.12, 0.08, 0.27], [-0.18, -0.1, 0.05, 0.26], [0.0, -0.3, -0.02, 0.2], [0.5, 0.12, 0.08, 0.22],
    [-0.52, 0.1, 0.06, 0.23], [0.26, 0.44, 0.1, 0.24], [-0.28, 0.44, 0.08, 0.25], [0, 0.12, -0.34, 0.3],
    [0.62, 0.32, -0.12, 0.2], [-0.64, 0.3, -0.12, 0.21], [0.0, 0.56, -0.05, 0.26],
  ];
  puffs.forEach(([x, y, z, r], i) => k4.lump(body, i % 3 === 2 ? cloudL : cloud, x, y, z, r * 1.05, r * 0.9, r, 0.6, 1, 0.25));
  for (const [x, y, z, r] of [[0.38, 0.32, -0.2, 0.3], [-0.4, 0.3, -0.18, 0.3], [0.0, -0.08, 0.28, 0.26], [0, 0.5, -0.3, 0.28]] as const) {
    k4.lump(body, haze, x, y, z, r * 1.2, r, r * 1.1, 0.6, 1, 0.3);
  }
  // Glimmers of the storm inside, where the puffs part.
  for (const [x, y, z, s] of [[0.2, 0.05, 0.34, 0.5], [-0.22, 0.25, 0.3, 0.45], [0.36, 0.3, 0.12, 0.4], [-0.35, -0.05, 0.2, 0.4], [0.02, -0.25, 0.18, 0.35]] as const) {
    glowSprite(body, '#b9d4ff', s, V(x, y, z), 0.5);
  }

  /* ---------- the veins of lightning: forks crawling over the cloud (redrawn every frame) ---------- */
  const veins: Fork[] = [];
  const vein = (pts: THREE.Vector3[], r: number): void => {
    veins.push(new Fork(body, pts, r, bolt, rng, 0.04));
  };
  const onCloud = (a: number, y: number, rr: number): THREE.Vector3 => V(Math.sin(a) * rr, y, Math.cos(a) * rr);
  for (let i = 0; i < 8; i++) {
    const a0 = -2.2 + i * 0.62 + (rng() - 0.5) * 0.3;
    const y0 = 0.45 - rng() * 0.2;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 5; j++) pts.push(onCloud(a0 + (rng() - 0.5) * 0.45, y0 - j * 0.15, 0.48 + (j % 2) * 0.05));
    vein(pts, 0.008);
  }

  /* ---------- the hood: deep, peaked, empty but for two pale points and the hint of a jaw ---------- */
  const hoodB = joint(body, V(0, 0.52, 0.12), 'hood');
  const hood = hoodB.abs;
  // The cowl: a shell open at the front (three's sphere puts phi = π/2 at +Z), deep and peaked.
  const gap = Math.PI * 0.5;
  const cowl = M(new THREE.SphereGeometry(1, 12, 8, Math.PI / 2 + gap / 2, Math.PI * 2 - gap, 0, Math.PI * 0.78), rag, hood);
  cowl.position.set(0, 0.7, 0.16);
  cowl.scale.set(0.24, 0.3, 0.26);
  cowl.rotation.x = 0.18;
  const peak = M(new THREE.ConeGeometry(0.13, 0.34, 7), rag, hood);
  peak.position.set(0, 0.98, 0.06);
  peak.rotation.x = -0.75;
  // The brim of the hood hanging forward over the dark.
  const brim = M(new THREE.TorusGeometry(0.2, 0.025, 4, 10, Math.PI), rag, hood);
  brim.position.set(0, 0.72, 0.36);
  brim.rotation.set(0.35, 0, 0);
  brim.scale.set(0.95, 1.15, 1);
  k4.swell(hood, voidM, 0, 0.66, 0.18, 0.18, 0.22, 0.17, 7, 5);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.022, 5, 3), bolt, hood).position.set(k * 0.055, 0.7, 0.34);
    glowSprite(hood, '#dbe8ff', 0.2, V(k * 0.055, 0.7, 0.37), 0.95);
  }
  // The jaw: a pale glint of teeth, low in the dark.
  const teeth = M(new THREE.BoxGeometry(0.07, 0.012, 0.01), bolt, hood);
  teeth.position.set(0, 0.59, 0.34);
  // The hood's rag runs down over the cloud like a mantle.
  for (let i = 0; i < 4; i++) {
    const a = -1.2 + i * 0.8;
    const p = k4.panel(hood, V(Math.sin(a) * 0.2, 0.6, Math.cos(a) * 0.12 + 0.12), 0.18, 0.45, 0.03, 1, rag, 0.5);
    p.rotation.set(0.2, a, 0);
  }

  /* ---------- arms of cloud: long, reaching, the claws crackling ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(body, V(k * 0.38, 0.38, 0.12), `arm${s}`);
    const ar = armB.abs;
    k4.lump(ar, cloud, k * 0.42, 0.36, 0.12, 0.16, 0.14, 0.15, 0.4, 1, 0.3);
    limb(ar, V(k * 0.42, 0.34, 0.14), V(k * 0.62, 0.08, 0.3), 0.11, 0.075, cloud, 6);
    k4.lump(ar, cloudL, k * 0.52, 0.22, 0.2, 0.11, 0.1, 0.11, 0.4, 0, 0.3);
    const foreB = joint(ar, V(k * 0.62, 0.08, 0.3), `fore${s}`);
    const fo = foreB.abs;
    k4.lump(fo, cloudL, k * 0.62, 0.08, 0.3, 0.09, 0.09, 0.09, 0.4, 0);
    limb(fo, V(k * 0.62, 0.08, 0.3), V(k * 0.66, -0.2, 0.46), 0.075, 0.045, cloud, 6);
    k4.lump(fo, cloud, k * 0.64, -0.08, 0.38, 0.08, 0.08, 0.08, 0.4, 0, 0.3);
    // A rag of the mantle trailing off the elbow.
    k4.panel(fo, V(k * 0.63, 0.06, 0.3), 0.1, 0.32, 0.01, 1, rag, 0.6).rotation.set(0, k * 1.2, 0);
    // Three long claws of black, and a crackle between them.
    for (let i = -1; i <= 1; i++) {
      limb(fo, V(k * 0.66, -0.2, 0.46), V(k * (0.66 + i * 0.05), -0.38, 0.52 + i * 0.03), 0.016, 0.004, voidM, 4);
    }
    k4.crack(fo, [V(k * 0.6, -0.36, 0.48), V(k * 0.66, -0.3, 0.5), V(k * 0.72, -0.36, 0.55)], 0.007, bolt, 0.03);
    glowSprite(fo, '#c9dcff', 0.22, V(k * 0.66, -0.3, 0.5), 0.75);
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  /* ---------- tendrils hanging under it, like rain from a squall ---------- */
  const tends: Record<string, THREE.Object3D> = {};
  TENDRILS.forEach((n, i) => {
    const a = (i / TENDRILS.length) * Math.PI * 2 + 0.4;
    const x = Math.sin(a) * 0.24, z = Math.cos(a) * 0.2 - 0.04;
    // Each sweeps out from under the cloud and trails back, the tip curling.
    const ox = Math.sin(a) * 0.14, back = -0.12 - rng() * 0.08;
    const p0 = V(x, -0.24, z), p1 = V(x + ox * 0.6, -0.42, z + back * 0.4), p2 = V(x + ox, -0.56, z + back);
    const t1 = joint(body, p0, `t${n}1`);
    k4.tube(t1.abs, [p0, p1, p2], 0.065 - i * 0.004, cloud, 5);
    const t2 = joint(t1.abs, p2, `t${n}2`);
    const len = 0.28 + rng() * 0.18;
    const p3 = p2.clone().add(V(ox * 0.5, -len * 0.55, back * 0.8)), p4 = p2.clone().add(V(ox * 0.3, -len, back * 1.8));
    k4.tube(t2.abs, [p2, p3, p4], 0.032, cloud, 4);
    k4.shard(t2.abs, cloud, p4, p4.clone().sub(p3), 0.12, 0.018);
    tends[`t${n}1`] = t1.p;
    tends[`t${n}2`] = t2.p;
  });
  // Streaks of rain falling off it (frames redraw them).
  const rain: Ribbon[] = [];
  for (let i = 0; i < 3; i++) {
    const a = rng() * 6.3, r = 0.25 + rng() * 0.15;
    rain.push(new Ribbon(body, [V(Math.sin(a) * r, -0.3, Math.cos(a) * r), V(Math.sin(a) * r, -0.6, Math.cos(a) * r - 0.05), V(Math.sin(a) * r * 1.1, -0.9, Math.cos(a) * r * 1.1 - 0.12)], 0.007, rainM, rng, 0.02));
  }

  /* ---------- the ball of lightning between its hands, and the fork it throws (both built tiny) ---------- */
  const chargeB = joint(body, V(0, 0.95, 0.35), 'charge');
  {
    const ball = G(chargeB.abs, 0, 0.95, 0.35);
    ball.scale.setScalar(CHARGE_REST);
    M(new THREE.IcosahedronGeometry(0.2, 0), bolt, ball);
    for (let i = 0; i < 5; i++) {
      const d = V(rng() - 0.5, rng() - 0.5, rng() - 0.5).normalize();
      k4.crack(ball, [V(0, 0, 0), d.clone().multiplyScalar(0.25), d.clone().multiplyScalar(0.45).add(V((rng() - 0.5) * 0.2, (rng() - 0.5) * 0.2, 0))], 0.02, bolt, 0.12);
    }
  }
  const boltB = joint(body, V(0, -0.2, 0.3), 'bolt');
  {
    const fork = G(boltB.abs, 0, -0.2, 0.3);
    fork.scale.setScalar(CHARGE_REST);
    // From the cloud down and forward to the ground ahead (its hover height), in jagged steps, with a branch.
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j <= 6; j++) pts.push(V((rng() - 0.5) * 0.18, -j * 0.22, j * 0.17));
    k4.crack(fork, pts, 0.03, bolt, 0.18);
    const b0 = pts[3] as THREE.Vector3;
    k4.crack(fork, [b0, b0.clone().add(V(0.2, -0.2, 0.05)), b0.clone().add(V(0.32, -0.45, 0.1))], 0.018, bolt, 0.12);
  }

  const t = (): void => {
    for (const v of veins) v.step();
    for (const r of rain) r.step();
  };
  t();
  return done(c, HERALD_HOVER + 1.05, t, undefined, bonesOf(c, {
    body: bodyB.p, hood: hoodB.p, armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
    ...tends, charge: chargeB.p, bolt: boltB.p,
  }));
};
